import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { setModal } from '../redux/modalSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGear, faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { hasUserPermission } from '../utils/permissionHelper'
import { selectUser } from '../redux/userSlice'
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { useRequest } from '../hooks/UseRequest'
import { selectPaymentScheduleData } from "../redux/paymentScheduleDataSlice"
import Loading from "./Loading";
import { fetchWithTimeoutAndRetry } from "../utils/fetchHelper";
import { selectPortfolios } from "../redux/portfolioSlice";
import { selectSubpanelRerender } from "../redux/rerenderSlice";
import { selectAuth } from "../redux/authSlice";

export default function CustomerServiceButtons(props) {
  const { children, module, disableEdit, submitUnlock, customer, currentDate = null } = props;
  const dispatch = useDispatch()
  const active = useSelector(selectUser)
  const portfolios = useSelector(selectPortfolios)
  const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
  const params = useParams()
  const requesting = useRef(false)
  const [responseData, setResponseData] = useState(null)
  const paymentScheduleData = useSelector(selectPaymentScheduleData)
  const [isLoaded, setIsLoaded] = useState(false)
  const navi = useNavigate()
  const request = useRequest()
  const rerenderValue = useSelector((state) => selectSubpanelRerender(state, 'customer_action_button'));
  const auth = useSelector(selectAuth)
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search);

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (active.status === 'succeeded' && auth.accessToken) {
      const pathSegments = window.location.pathname.split('/');
      const moduleName = pathSegments[1];
      const allowedModules = ['manual_verification', 'customer', 'payment_schedule'];
      if (params?.id !== undefined && allowedModules.includes(moduleName)) {
        getActionButtonOption(params.id)
      }
    }
  }, [active, params?.id, currentDate, rerenderValue, auth])

  const getActionButtonOption = async (customerId) => {
    if (requesting.current) return;
    const loanGuid = queryParams.get('loan');
    const leadGuid = queryParams.get('lead');

    requesting.current = true;
    try {
      const params = new URLSearchParams();
      if (currentDate) {
        params.append('current_date', currentDate);
      }
      if (loanGuid) {
        params.append('loan', loanGuid);
      }
      if (leadGuid) {
        params.append('lead', leadGuid);
      }

      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/action_button_option?${params.toString()}`;

      request.setEndpoint(endpoint)
      // Mount-critical — see fetchHelper.js. Gates the cog dropdown content.
      const response = await fetchWithTimeoutAndRetry(() => request.get())
      setResponseData(response?.data?.data);
      requesting.current = false;
      setIsLoaded(true);
    } catch (error) {
      setResponseData([]);
      // dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      requesting.current = false;
    }
  }

  const handleDeceasedClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'deceased_modal',
        options: { customerId: params.id, successCallback: () => getActionButtonOption(params.id) }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleCeaseDesistClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'cease_desist_modal',
        options: { customerId: params.id }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleACHRevocationClick = (hasAchRevoked, loanId, hasBkConfirmed) => {
    dispatch(
      setModal({
        is_active: true,
        type: 'ach_revocation_modal',
        options: { customerId: params.id, successCallback: () => getActionButtonOption(params.id), hasAchRevoked: hasAchRevoked, loanId: loanId, hasBkConfirmed: hasBkConfirmed }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleWithdrawLeadClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'withdraw_lead_modal',
        options: { customerId: params.id, successCallback: () => getActionButtonOption(params.id) }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleScheduleCallbackClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'schedule_callback_modal',
        options: { customerId: params.id }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handlePreferredCommunicationsClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'preferred_communications_modal',
        options: { customerId: params.id }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleCustomerDetailsClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'customer_details_modal',
        options: { customerId: params.id }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handlePortfolioDetailsClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'portfolio_details_modal',
        options: { portfolioId: responseData?.customer_portfolio_id, customerId: params.id }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleLeadDetailsClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'lead_details_modal',
        options: { latestLeadId: responseData?.latest_lead_id, customerId: params.id }
      })
    )
    handleClick()
  }

  const handleUpdateSSNClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'customer_ssn_modal',
        options: { customerId: params.id }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleUpdateBankAccountClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'bank_account_modal',
        options: { customerId: params.id }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleAddDebitCardClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'debit_card_modal',
        options: { customerId: params.id, customer: customer }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handlePowerOfAttorneyClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'power_of_attorney_modal',
        options: { customerId: params.id, can_poa: responseData?.can_poa, successCallback: () => getActionButtonOption(params.id) }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleDmcClick = (loanId) => {
    dispatch(
      setModal({
        is_active: true,
        type: 'dmc_modal',
        options: {
          customerId: params.id,
          successCallback: () => getActionButtonOption(params.id),
          loanId: loanId,
          loanStatusDropdownOptionId: paymentScheduleData?.paymentScheduleData?.loan_status_dropdown_option_id
        }
      })
    )
    submitUnlock(1)
    handleClick()
  }
  const handleIdentityTheftClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'identity_theft_modal',
        options: {
          identity_theft_status_dropdown_option_id: responseData?.identity_theft_status_dropdown_option_id,
          customerId: params.id,
          latest_loan_id: responseData?.latest_loan_id,
          successCallback: () => getActionButtonOption(params.id)
        }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleCancelRefinanceClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'cancel_refinance_modal',
        options: {
          customerId: params.id,
          latest_loan_id: responseData?.latest_loan_id,
          successCallback: () => getActionButtonOption(params.id)
        }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleVoidLoanClick = (loanId) => {
    dispatch(
      setModal({
        is_active: true,
        type: 'void_loan_modal',
        options: {
          customerId: params.id,
          successCallback: () => getActionButtonOption(params.id),
          loanId: loanId
        }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleWaiveLoanClick = (loanId) => {
    dispatch(
      setModal({
        is_active: true,
        type: 'waive_loan_modal',
        options: {
          customerId: params.id,
          successCallback: () => getActionButtonOption(params.id),
          loanId: loanId
        }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleBankruptcyAttorneyInformationModal = (loanId) => {
    dispatch(setModal({
      is_active: true,
      type: 'bankruptcy_attorney_information_modal',
      options: {
        customerId: params.id,
        successCallback: () => getActionButtonOption(params.id),
        loanId: loanId
      }
    }));
  }

  const handleResolutionClick = (loanId) => {
    dispatch(setModal({
      is_active: true,
      type: 'resolution_amount_modal',
      options: {
        customerId: params.id,
        loanId: loanId,
        successCallback: () => getActionButtonOption(params.id),
      },
    }));
  }

  const handleCancelLoanClick = (loanId) => {
    if (!responseData?.can_cancel_loan) {
      return;
    }
    dispatch(setModal({
      is_active: true,
      type: 'cancel_loan_modal',
      options: {
        customerId: params.id,
        successCallback: () => getActionButtonOption(params.id),
        loanId: loanId
      }
    }));
    submitUnlock(1)
    handleClick()
  }

  const handleDeleteBureauTradelines = () => {
    if (!hasUserPermission(activePermissions, 'can_delete_bureau_tradelines')) {
      return;
    }
    dispatch(
      setModal({
        is_active: true,
        type: 'delete_bureau_tradelines',
        options: { customerId: params.id }
      })
    )
    handleClick()
  }

  const handleExpressLoanClick = () => {
    if (!responseData?.can_express_loan) {
      return;
    }
    dispatch(setModal({
      is_active: true,
      type: 'express_loan',
      options: {
        customerId: params.id
      }
    }));
  }

  const handleRefinanceCashOutClick = (loanId) => {
    if (!responseData?.can_refinance_cash_out) {
      return;
    }
    dispatch(
      setModal({
        is_active: true,
        type: 'refinance_cash_out_modal',
        options: {
          loanId: loanId,
          customerId: params.id
        }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleCollectionRefinanceClick = (loanId) => {
    if (!responseData?.can_collections_refinance) {
      return;
    }
    dispatch(
      setModal({
        is_active: true,
        type: 'collections_refinance_modal',
        options: {
          loanId: loanId,
          customerId: params.id
        }
      })
    )
    submitUnlock(1)
    handleClick()
  }

  const handleRefinanceCashOutEligibilityClick = (loanId) => {
    dispatch(
      setModal({
        is_active: true,
        type: 'cash_out_refinance_eligibility_modal',
        options: {
          loanId: loanId
        }
      })
    )
    handleClick();
  }

  const handleNoCashOutRefiEligibilityClick = (loanId) => {
    if (!responseData?.can_refinance || !loanId) return;
    dispatch(
      setModal({
        is_active: true,
        type: 'no_cash_out_refinance_eligibility_modal',
        options: {
          loanId: loanId,
          customerId: params.id
        }
      })
    )
    handleClick();
  }

  const handleMergeCustomerToolClick = () => {
    dispatch(
      setModal({
        is_active: true,
        type: 'customer_search_modal',
        options: { customerId: params.id }
      })
    )
    handleClick()
  }

  const [isActive, setIsActive] = useState(false);
  const node = useRef();

  const handleClick = () => {
    setIsActive(!isActive);
  }

  const handleClickOutside = (event) => {
    if (node.current.contains(event.target)) {
      return;
    }
    setIsActive(false);
  };

  const disabledAddDebitCard = () => {
    if (customer?.portfolio_id) {
      const portfolio = portfolios.data.filter((o) => o.id === customer.portfolio_id)[0];
      return !portfolio.is_debit_card_payment;
    }
    return false;
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="header-button-sub-container">
      <div className={`dropdown ${isActive ? 'is-active' : ''}`} ref={node}>
        <div className="dropdown-trigger">
          <button
            type="button"
            className="button has-background-grey has-text-white is-small mr-1 px-2 header-button-icon"
            onClick={handleClick}
            disabled={disableEdit}
            data-testid="customer-service-actions-trigger"
            aria-label="Customer service actions"
          >
            <FontAwesomeIcon icon={faGear} />
          </button>
        </div>
        <div className="dropdown-menu dropdown-menu-customer-actions" id="dropdown-menu" role="menu" data-testid="customer-service-actions-menu">
          <div className="dropdown-content" style={{ maxHeight: '85vh', overflowX: "auto" }}>
            {['manual_verification', 'customer', 'payment_schedule'].includes(module) && isLoaded && (
              <>
                <div className="dropdown-item">
                  <p><strong>Customer Actions</strong></p>
                </div>
                {hasUserPermission(activePermissions, 'can_add_debit_card') && (
                  <a
                    className={`dropdown-item ${disabledAddDebitCard() ? 'disabled-menu-item' : ''}`}
                    onClick={disabledAddDebitCard() ? null : handleAddDebitCardClick}
                    style={disabledAddDebitCard() ? { pointerEvents: 'none', color: 'grey' } : {}}
                  >
                    Add Debit Card
                    {disabledAddDebitCard() && (
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={`Debit card usage for payment is not permitted for this customer's portfolio.\n portfolio.is_debit_card_payment must be 1`}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}
                {hasUserPermission(activePermissions, 'quick_action_cease_desist') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={handleCeaseDesistClick}
                  >
                    Cease & Desist
                  </a>
                )}
                {hasUserPermission(activePermissions, 'customer_update') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={handleCustomerDetailsClick}
                  >
                    Customer Details
                  </a>
                )}
                {hasUserPermission(activePermissions, 'quick_action_deceased') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={handleDeceasedClick}
                  >
                    Deceased
                  </a>
                )}
                {hasUserPermission(activePermissions, 'merge_customer') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={handleMergeCustomerToolClick}
                  >
                    Merge Customer
                  </a>
                )}
                {hasUserPermission(activePermissions, 'quick_action_poa') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={handlePowerOfAttorneyClick}
                  >
                    Power of Attorney
                  </a>
                )}
                {hasUserPermission(activePermissions, 'customer_update') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={handlePreferredCommunicationsClick}
                  >
                    Preferred Communications
                  </a>
                )}
                {hasUserPermission(activePermissions, 'can_update_bank_account') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_add_new_bank_account ? () => handleUpdateBankAccountClick() : null}
                    style={responseData?.can_add_new_bank_account ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Update Bank Account Information
                    {responseData?.can_add_new_bank_account_explanation && (
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.can_add_new_bank_account_explanation}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}
                {hasUserPermission(activePermissions, 'can_update_ssn') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={handleUpdateSSNClick}
                  >
                    Update SSN
                  </a>
                )}

                <hr className="dropdown-divider" />

                <div className="dropdown-item">
                  <p><strong>Lead Actions</strong></p>
                </div>

                <a
                  className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                  onClick={hasUserPermission(activePermissions, 'is_manager') ? () => handleLeadDetailsClick() : null}
                  style={hasUserPermission(activePermissions, 'is_manager') ? {} : { pointerEvents: 'none', color: 'grey' }}
                >
                  Lead Details
                  {!hasUserPermission(activePermissions, 'is_manager') && (
                    <span
                      style={{ pointerEvents: 'auto' }}
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                      data-tooltip={"Only manager can do this action"}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`pl-1 has-text-link`}
                      />
                    </span>
                  )}
                </a>

                {hasUserPermission(activePermissions, 'quick_action_withdraw_lead') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_withdraw_lead ? () => handleWithdrawLeadClick() : null}
                    style={responseData?.can_withdraw_lead ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Withdraw Lead
                    {responseData?.can_not_withdraw_lead_reason && (
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.can_not_withdraw_lead_reason}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}

                {hasUserPermission(activePermissions, 'quick_action_schedule_callback') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    data-testid="quick-action-schedule-callback"
                    onClick={() => handleScheduleCallbackClick()}
                  >
                    Schedule Callback Time
                  </a>
                )}

                <hr className="dropdown-divider" />

                <div className="dropdown-item">
                  <p><strong>Loan Actions</strong></p>
                </div>

                {(hasUserPermission(activePermissions, 'quick_action_ach_revocation') ||
                  hasUserPermission(activePermissions, 'quick_action_ach_revocation_update')) && (
                    <a
                      data-testid="quick-action-ach-revocation"
                      className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                      onClick={responseData?.can_ach ? () => handleACHRevocationClick(responseData?.has_ach_revoked, responseData?.latest_loan_id, responseData?.has_bk_confirmed) : null}
                      style={((hasUserPermission(activePermissions, 'quick_action_ach_revocation') && responseData?.can_ach) || (hasUserPermission(activePermissions, 'quick_action_ach_revocation_update') && responseData?.has_ach_revoked)) ? {} : { pointerEvents: 'none', color: 'grey' }}
                    >
                      ACH Revocation
                      {responseData?.ach_revoke_explanation && (
                        <span
                          data-testid="quick-action-ach-revocation-explanation"
                          style={{ pointerEvents: 'auto' }}
                          className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                          data-tooltip={responseData?.ach_revoke_explanation}
                        >
                          <FontAwesomeIcon
                            icon={faCircleInfo}
                            className={`pl-1 has-text-link`}
                          />
                        </span>
                      )}
                    </a>
                  )}
                {hasUserPermission(activePermissions, 'quick_action_bankruptcy') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_bankruptcy ? () => handleBankruptcyAttorneyInformationModal(responseData?.latest_loan_id) : null}
                    style={responseData?.can_bankruptcy ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Bankruptcy
                    {responseData?.bankruptcy_explanation && (
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.bankruptcy_explanation}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}
                {hasUserPermission(activePermissions, 'plc_cancel_loan') && (
                  <a
                    data-testid="quick-action-plc-cancel-loan"
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_cancel_loan ? () => handleCancelLoanClick(responseData?.latest_loan_id) : null}
                    style={responseData?.can_cancel_loan ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Cancel Loan
                    {responseData?.cancel_loan_explanation && (
                      <span
                        data-testid="quick-action-plc-cancel-loan-explanation"
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.cancel_loan_explanation}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}
                {hasUserPermission(activePermissions, 'quick_action_dmc') && (
                  <a
                    data-testid="quick-action-dmc"
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_dmc_quick_action ? () => handleDmcClick(responseData?.latest_loan_id) : null}
                    style={responseData?.can_dmc_quick_action ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Debt Management Company
                    {responseData?.dmc_qa_explanation && (
                      <span
                        data-testid="quick-action-dmc-explanation"
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.dmc_qa_explanation}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}
                {hasUserPermission(activePermissions, 'can_delete_bureau_tradelines') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={hasUserPermission(activePermissions, 'can_delete_bureau_tradelines') ? () => handleDeleteBureauTradelines() : null}
                    style={hasUserPermission(activePermissions, 'can_delete_bureau_tradelines') ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Delete Bureau Tradeline
                  </a>
                )}
                {hasUserPermission(activePermissions, 'can_express_loan') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_express_loan ? () => handleExpressLoanClick() : null}
                    style={responseData?.can_express_loan ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Express Loan
                    {responseData?.express_loan_explanation && (
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.express_loan_explanation}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}
                {(hasUserPermission(activePermissions, 'quick_action_id_theft_suspect_update') ||
                  hasUserPermission(activePermissions, 'quick_action_id_theft_confirm_update') || hasUserPermission(activePermissions, 'quick_action_id_theft_none_update')) && (
                    <a
                      data-testid="quick-action-id-theft"
                      className={`dropdown-item ${(disableEdit || !responseData?.latest_loan_id) ? 'disabled-menu-item' : ''} `}
                      onClick={responseData?.latest_loan_id ? () => { handleIdentityTheftClick() } : null}
                      style={responseData?.latest_loan_id ? {} : { pointerEvents: 'none', color: 'grey' }}
                    >
                      Identity Theft
                    </a>
                  )}
                {hasUserPermission(activePermissions, 'can_refinance_loan_cashout') && hasUserPermission(activePermissions, 'quick_action_cash_out_eligibility') && (
                  <a
                    className={`dropdown-item ${(disableEdit || !responseData?.latest_loan_id) ? 'disabled-menu-item' : ''} `}
                    onClick={(responseData?.can_refinance_cash_out && responseData?.latest_loan_id) ? () => { handleRefinanceCashOutClick(responseData?.latest_loan_id) } : null}
                    style={(responseData?.can_refinance_cash_out && responseData?.latest_loan_id) ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Refinance - Cash Out
                    <span
                      style={{ pointerEvents: 'auto' }}
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                      data-tooltip={(responseData.refinance_cash_out_explanation) ? responseData.refinance_cash_out_explanation : "To see if the customer passes the initial check to determine if they can add additional principal to their current loan."}
                    >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                  </a>
                )}
                {hasUserPermission(activePermissions, 'can_refinance_loan_nocash') && (
                  <a
                    data-testid="quick-action-refinance-cancellation"
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_cancel_refi ? () => { handleCancelRefinanceClick() } : null}
                    style={responseData?.can_cancel_refi ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Refinance - Cancellation
                    {responseData?.can_cancel_refi_explanation && (
                      <span
                        data-testid="quick-action-refinance-cancellation-explanation"
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.can_cancel_refi_explanation}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}
                {hasUserPermission(activePermissions, 'quick_action_cash_out_eligibility') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_refinance_cash_out ? () => { handleRefinanceCashOutEligibilityClick(responseData?.latest_loan_id) } : null}
                    style={responseData?.can_refinance_cash_out ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Refinance - Check Eligibility
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={(responseData.refinance_cash_out_explanation) ? responseData.refinance_cash_out_explanation : "To see if the customer passes the initial check to determine if they can add additional principal to their current loan."}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                  </a>
                )}

                {hasUserPermission(activePermissions, 'quick_action_collections_refi') && (
                  <a
                    className={`dropdown-item ${(disableEdit || !responseData?.latest_loan_id) ? 'disabled-menu-item' : ''} `}
                    onClick={(responseData?.can_collections_refinance && responseData?.latest_loan_id) ? () => { handleCollectionRefinanceClick(responseData?.latest_loan_id) } : null}
                    style={(responseData?.can_collections_refinance && responseData?.latest_loan_id) ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Refinance - Collections
                    {(responseData.collections_refinance_explanation != '') ? (
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData.collections_refinance_explanation}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    ) : (
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={"To refinance the customer’s current loan into a new loan based on payment amount."}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}

                {hasUserPermission(activePermissions, 'can_refinance_loan_nocash') && (
                  <a
                    data-testid="quick-action-refinance-no-cash-out"
                    className={`dropdown-item ${(disableEdit || !responseData?.latest_loan_id) ? 'disabled-menu-item' : ''}`}
                    onClick={(responseData?.can_refinance && responseData?.latest_loan_id) ? () => handleNoCashOutRefiEligibilityClick(responseData?.latest_loan_id) : null}
                    style={(responseData?.can_refinance && responseData?.latest_loan_id) ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Refinance - No-Cash Out
                    {responseData?.refinance_explanation && (
                      <span
                        data-testid="quick-action-refinance-no-cash-out-explanation"
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.refinance_explanation}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}
                {hasUserPermission(activePermissions, 'quick_action_resolution') && (
                  <a
                    data-testid="quick-action-resolution"
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_send_loan_to_resolution ? () => handleResolutionClick(responseData?.latest_loan_id) : null}
                    style={responseData?.can_send_loan_to_resolution ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Resolution
                    {responseData?.send_to_resolution_explanation && (
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.send_to_resolution_explanation}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}

                {hasUserPermission(activePermissions, 'quick_action_void') && (
                  <a
                    data-testid="quick-action-void"
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_void_loan ? () => handleVoidLoanClick(responseData?.latest_loan_id) : null}
                    style={responseData?.can_void_loan ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Void Loan
                    {responseData?.can_not_void_loan_explanation && (
                      <span
                        data-testid="quick-action-void-explanation"
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.can_not_void_loan_explanation}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}

                {hasUserPermission(activePermissions, 'quick_action_waive') && (
                  <a
                    className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                    onClick={responseData?.can_waive_loan ? () => handleWaiveLoanClick(responseData?.latest_loan_id) : null}
                    style={responseData?.can_waive_loan ? {} : { pointerEvents: 'none', color: 'grey' }}
                  >
                    Waive(Legal) Loan
                    {responseData?.can_not_waive_loan_reason && (
                      <span
                        style={{ pointerEvents: 'auto' }}
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-top has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu`}
                        data-tooltip={responseData?.can_not_waive_loan_reason}
                      >
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className={`pl-1 has-text-link`}
                        />
                      </span>
                    )}
                  </a>
                )}

                {hasUserPermission(activePermissions, 'quick_action_portfolio') && (
                  <>
                    <hr className="dropdown-divider" />

                    <div className="dropdown-item">
                      <p><strong>Portfolio Actions</strong></p>
                    </div>

                    <a
                      className={`dropdown-item ${disableEdit ? 'disabled-menu-item' : ''}`}
                      onClick={handlePortfolioDetailsClick}
                    >
                      Portfolio Details
                    </a>
                  </>
                )}
              </>
            )}
            {['manual_verification', 'customer', 'payment_schedule'].includes(module) && !isLoaded && (
              <Loading size="is-size-5" />)}
          </div>
        </div>
      </div>
      {children}
    </div>
  )
}
